/* IBM Confidential
 *
 * OCO Source Materials
 *
 * IBM Cognos Products: ba-ui-toolkit
 * (C) Copyright IBM Corp. 2019
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U.S. Copyright Office.
 * NLS_ENCODING=UTF-8
 * NLS_MESSAGEFORMAT_NONE
 */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';
import { DropTarget as dropTarget } from 'react-dnd';
import DragLayer from '@ba-ui-toolkit/draglayer';
import itemDnD, { ITEM_TYPE, scrollableDnD } from '@ba-ui-toolkit/itemdnd';
import { lodash } from '@ba-ui-toolkit/utils';

export const treeItemDnD = (TreeItem, itemType = ITEM_TYPE) => {
	const options =
		typeof itemType === 'string'
			? { dragItemType: itemType, dropItemType: itemType }
			: itemType;

	return itemDnD(TreeItem, options);
};

const { omit } = lodash;

const dropTargetMethods = {
	canDrop(props, monitor) {
		if (
			props.canDrop &&
			(monitor.isOver({ shallow: true }) || (props.canFallThrough && props.canFallThrough()))
		) {
			return props.canDrop({ data: props.data }, monitor.getItem(), monitor);
		}

		return false;
	},

	hover() {},

	drop(props, monitor) {
		if (props.onDrop && !monitor.didDrop()) {
			props.onDrop({ data: props.data }, monitor.getItem(), monitor);
		}
	},
};

export default function(Tree, itemType = ITEM_TYPE, TreeDragLayer = DragLayer) {
	const ScrollableTree = scrollableDnD(Tree);

	@dropTarget(itemType, dropTargetMethods, (connect, monitor) => ({
		connectDropTarget: connect.dropTarget(),
		isActive: () => monitor.isOver() && monitor.canDrop(),
		getDragPosition: () => monitor.getClientOffset(),
	}))
	class TreeDnD extends Component {
		static propTypes = {
			data: PropTypes.any.isRequired,
			dragLayerRenderer: PropTypes.func,
			canDrop: PropTypes.func,
			isActive: PropTypes.func,
			getDragPosition: PropTypes.func,
			onDrop: PropTypes.func,
			itemRenderer: PropTypes.func.isRequired,
			connectDropTarget: PropTypes.func.isRequired,
			theme: PropTypes.string,
			selectionMode: PropTypes.string,
			activeClassName: PropTypes.string,
			className: PropTypes.string,
			onEndDrag: PropTypes.func,
			onBeginDrag: PropTypes.func,
			canDragNode: PropTypes.func,
			onItemHover: PropTypes.func,
			canFallThrough: PropTypes.func,
			treeDnDForwardRef: PropTypes.func,
		};

		static defaultProps = {
			canFallThrough: () => false,
		};

		state = {
			draggingNodes: [],
		};

		_connectToDnD = ref => {
			this._node = ReactDOM.findDOMNode(ref);
			this.props.connectDropTarget(this._node);
			this._tree = ref;
			const { treeDnDForwardRef } = this.props;
			treeDnDForwardRef && treeDnDForwardRef(ref);
		};

		_dragLayerRenderer = ({ itemProps, currentOffset, droppable }) => (
			<TreeDragLayer currentOffset={currentOffset} droppable={droppable}>
				{this.props.dragLayerRenderer(itemProps, droppable)}
			</TreeDragLayer>
		);

		_getDropProps({ node, data, expanded, isAlwaysExpanded }, droppedY) {
			return {
				item: node,
				data,
				[typeof droppedY === 'boolean' ? 'droppedBelow' : 'droppedYPercent']: droppedY,
				expanded: expanded || isAlwaysExpanded,
			};
		}

		_itemRenderer = (node, props) => {
			const { itemRenderer, canDrop, onDrop, onItemHover } = this.props;
			const { draggingNodes } = this.state;
			const itemDragging = draggingNodes.indexOf(node) >= 0;

			const treeProps = {
				...props,
				dragging: itemDragging,
				canDrop,
				onDrop,
				canDragNode: this._canDragNode,
				getDropProps: this._getDropProps,
				onBeginDrag: this._onBeginDrag,
				onEndDrag: this._onEndDrag,
				onHover: onItemHover,
			};

			return itemRenderer(node, treeProps);
		};

		_canDragNode = itemProps => {
			const { canDragNode } = this.props;
			const { node } = itemProps;
			const selected = this._tree.getSelectedNodes();

			return selected.indexOf(node) !== -1 && (!canDragNode || canDragNode(node));
		};

		_onBeginDrag = itemProps => {
			const { data } = this.props;
			const { node } = itemProps;

			const selected = this._tree.getSelectedNodes();
			this.setState({ draggingNodes: selected });

			const payload = {
				node,
				items: selected,
				dragLayerRenderer: this._dragLayerRenderer,
				data,
			};

			let newPayload;

			if (this.props.onBeginDrag) {
				newPayload = this.props.onBeginDrag(payload);
			}

			return newPayload == null ? payload : newPayload;
		};

		// setTimeout 0 is required here to ensure that drag end state gets set after click events are fired
		_onEndDrag = () => {
			setTimeout(() => {
				this.setState({ draggingNodes: [] });
				this.props.onEndDrag && this.props.onEndDrag();
			}, 0);
		};

		render() {
			const { draggingNodes } = this.state;
			const { selectionMode, activeClassName, isActive, ...other } = this.props;
			const passThroughProps = omit(
				other,
				'className',
				'onDrop',
				'canDrop',
				'dragLayerRenderer',
				'theme',
				'connectDropTarget',
				'onBeginDrag',
				'canDragNode',
				'onEndDrag',
				'canFallThrough',
				'onItemHover',
				'getDragPosition',
				'treeDnDForwardRef'
			);

			let { className } = this.props;

			if (activeClassName && isActive()) {
				className = className ? `${className} ${activeClassName}` : activeClassName;
			}

			return (
				<ScrollableTree
					fullHeight
					{...passThroughProps}
					className={className}
					itemRenderer={this._itemRenderer}
					selectionMode={
						draggingNodes.length && selectionMode !== 'none'
							? 'disabled'
							: selectionMode
					}
					ref={this._connectToDnD}
				/>
			);
		}
	}

	return React.forwardRef((props, ref) => <TreeDnD {...props} treeDnDForwardRef={ref} />);
}
